/*
 * neydo-shopify cart-ajax.js
 *
 * <cart-controller> — autonomous custom element that owns all Shopify cart
 * AJAX logic. It replaces the outermost div of the cart-modal section so
 * no extra DOM nesting is introduced.
 *
 * Refresh strategy — surgical, not full-innerHTML:
 *   • /cart.js + /?sections=cart-modal are fetched in parallel.
 *   • Only the affected [data-cart-item] node(s) and .cart-block__bottom
 *     are swapped in the live DOM; untouched items never re-render.
 *   • Full innerHTML swap only when the cart transitions to/from empty.
 *
 * Event flow:
 *   1. form[data-product-form] submit (document-level capture)
 *      → POST /cart/add.js → _refreshSurgical({ changedKey }) + _open()
 *   2. click [data-cart-remove] (scoped to this)
 *      → POST /cart/change.js { quantity: 0 } → _refreshSurgical({ removedKey })
 *   3. click [data-counter-increment/decrement] (scoped to this)
 *      → read updated input on next tick → _scheduleUpdate()
 *   4. change input[data-cart-line] (scoped to this)
 *      → _scheduleUpdate() (debounced 300 ms → /cart/update.js
 *         → _refreshSurgical({ changedKeys }))
 */
class CartController extends HTMLElement {
  connectedCallback() {
    this._ac = new AbortController()
    const sig = this._ac.signal

    this._pending = {}
    this._updateTimer = null

    document.addEventListener('submit', this._onFormSubmit.bind(this), {
      capture: true,
      signal: sig,
    })
    this.addEventListener('click', this._onClick.bind(this), { signal: sig })
    this.addEventListener('change', this._onChange.bind(this), { signal: sig })

    this._fetchCart()
      .then(c => this._updateHeaderCount(c))
      .catch(() => {})
  }

  disconnectedCallback() {
    this._ac?.abort()
    clearTimeout(this._updateTimer)
  }

  // ── helpers ──────────────────────────────────────────────────────────────

  _updateHeaderCount(cart) {
    document
      .querySelectorAll('.cart-button__count, .cart-quantity, [data-cart-count]')
      .forEach(el => { el.textContent = cart.item_count })
  }

  _open() {
    if (typeof window.openModal === 'function') { window.openModal('cart'); return }
    this.classList.add('is-open')
    this.setAttribute('aria-hidden', 'false')
  }

  async _fetchCart() {
    const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } })
    if (!res.ok) throw new Error('cart fetch failed: ' + res.status)
    return res.json()
  }

  // ── surgical refresh ──────────────────────────────────────────────────────
  //
  // options:
  //   removedKey  — key of the item that was deleted (remove from DOM immediately)
  //   changedKey  — single key whose <li> should be swapped from fresh HTML
  //   changedKeys — array of keys (used when flushing multiple pending updates)
  //
  // /cart.js and /?sections=cart-modal are fetched in parallel.
  // Only [data-cart-item=key] nodes and .cart-block__bottom are touched.
  // Full innerHTML swap only when cart_count hits 0 or list didn't exist yet.

  async _refreshSurgical({ removedKey = null, changedKey = null, changedKeys = null } = {}) {
    try {
      const [cart, sectionRes] = await Promise.all([
        this._fetchCart(),
        fetch('/?sections=cart-modal', { headers: { Accept: 'application/json' } }),
      ])

      this._updateHeaderCount(cart)
      this.classList.toggle('modal-cart--empty', cart.item_count === 0)

      if (!sectionRes.ok) return cart
      const data = await sectionRes.json()
      const html = data['cart-modal']
      if (!html) return cart

      const doc = new DOMParser().parseFromString(html, 'text/html')
      const fresh =
        doc.querySelector('[data-modal="cart"]') ||
        doc.body.querySelector('.modal-cart')
      if (!fresh) return cart

      // Full swap: cart is now empty, or was empty and gained its first item
      const currentList = this.querySelector('.cart-block__list')
      if (cart.item_count === 0 || !currentList) {
        const wasOpen = this.classList.contains('is-open')
        this.innerHTML = fresh.innerHTML
        if (wasOpen) this.classList.add('is-open')
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }))
        return cart
      }

      // Remove deleted item from current DOM
      if (removedKey) {
        this.querySelector(`[data-cart-item="${CSS.escape(removedKey)}"]`)?.remove()
      }

      // Swap / insert changed line items
      const keys = changedKeys ?? (changedKey ? [changedKey] : [])
      for (const key of keys) {
        const freshItem = fresh.querySelector(`[data-cart-item="${CSS.escape(key)}"]`)
        const currentItem = this.querySelector(`[data-cart-item="${CSS.escape(key)}"]`)
        if (freshItem && currentItem) {
          currentItem.replaceWith(freshItem)
        } else if (freshItem) {
          // Newly added item not yet in the drawer
          currentList.appendChild(freshItem)
        }
      }

      // Footer always changes (subtotal, stock state may differ)
      const freshFooter = fresh.querySelector('.cart-block__bottom')
      const currentFooter = this.querySelector('.cart-block__bottom')
      if (freshFooter && currentFooter) currentFooter.replaceWith(freshFooter)

      document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }))
      return cart
    } catch (e) {
      console.error('[cart] refresh failed:', e)
    }
  }

  // ── add to cart ───────────────────────────────────────────────────────────

  async _onFormSubmit(e) {
    const form = e.target.closest('form[data-product-form]')
    if (!form) return
    e.preventDefault()

    const formData = new FormData(form)
    if (!formData.get('quantity')) formData.set('quantity', '1')
    if (!formData.get('id')) {
      console.warn('[cart] form missing variant id', form)
      return
    }

    const btn = form.querySelector('[data-product-button]')
    if (btn) btn.disabled = true

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/javascript' },
        body: formData,
      })
      if (res.ok) {
        const addedItem = await res.json()
        await this._refreshSurgical({ changedKey: addedItem.key })
        this._open()
        const panel = form.closest('[data-product-panel]')
        if (panel) {
          panel.classList.add('is-added')
          const timeout = parseInt(panel.getAttribute('data-added-timeout'), 10) || 4000
          setTimeout(() => panel.classList.remove('is-added'), timeout)
        }
      } else {
        const err = await res.json().catch(() => ({ description: 'cart add failed' }))
        console.error('[cart] add failed:', err)
        alert(err.description || 'Could not add to cart')
      }
    } catch (err) {
      console.error('[cart] add error:', err)
    } finally {
      if (btn) btn.disabled = false
    }
  }

  // ── remove + counter ──────────────────────────────────────────────────────

  async _onClick(e) {
    const removeBtn = e.target.closest('[data-cart-remove]')
    if (removeBtn) {
      e.preventDefault()
      const key = removeBtn.getAttribute('data-cart-remove')
      if (!key) return
      try {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: key, quantity: 0 }),
        })
        await this._refreshSurgical({ removedKey: key })
      } catch (err) {
        console.error('[cart] remove error:', err)
      }
      return
    }

    const counterBtn = e.target.closest('[data-counter-increment], [data-counter-decrement]')
    if (!counterBtn) return
    const container = counterBtn.closest('[data-counter-container]')
    if (!container) return
    const input = container.querySelector('input[data-cart-line]')
    if (!input) return
    // Defer one tick so the dist counter logic updates the input value first
    setTimeout(() => {
      const result = this._readQty(input)
      if (!result) return
      this._scheduleUpdate(result.key, result.qty)
    }, 30)
  }

  // ── quantity input change ─────────────────────────────────────────────────

  // Reads qty from input, clamps it to [min, max] and writes back if clamped.
  // Returns null when the value is unusable (NaN, missing key).
  _readQty(input) {
    const key = input.getAttribute('data-cart-line')
    if (!key) return null
    let qty = parseInt(input.value, 10)
    if (isNaN(qty) || qty < 0) return null
    const min = input.min ? Math.max(1, parseInt(input.min, 10)) : 1
    const max = input.max ? parseInt(input.max, 10) : Infinity
    if (qty > max) { qty = max; input.value = max }
    if (qty < min) { qty = min; input.value = min }
    return { key, qty }
  }

  _onChange(e) {
    const input = e.target.closest('input[data-cart-line]')
    if (!input) return
    const result = this._readQty(input)
    if (!result) return
    this._scheduleUpdate(result.key, result.qty)
  }

  _scheduleUpdate(key, qty) {
    this._pending[key] = qty
    clearTimeout(this._updateTimer)
    this._updateTimer = setTimeout(() => this._flushUpdates(), 300)
  }

  async _flushUpdates() {
    const updates = this._pending
    this._pending = {}
    const keys = Object.keys(updates)
    if (!keys.length) return
    try {
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates }),
      })
      await this._refreshSurgical({ changedKeys: keys })
    } catch (err) {
      console.error('[cart] update error:', err)
    }
  }
}

customElements.define('cart-controller', CartController)
